import { useChatScroll } from '@/components/chat/useChatScroll';
import { useState, useEffect, useRef, useCallback } from 'react';
import { Brain, Send, Plus, ThumbsUp, ThumbsDown, Activity, History, Sparkles } from 'lucide-react';
import { BrainMarkdown } from '@/components/brain/BrainMarkdown';
import { AttachmentBar } from '@/components/brain/AttachmentBar';
import { SkillPickerPopover } from '@/components/brain/SkillPickerPopover';
import { FilePicker } from '@/components/brain/FilePicker';
import type { AttachedContextFile } from '../../../../shared/types/brain';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/SharedAuthContext';
import { fetchAuthSession } from 'aws-amplify/auth';
import { trpc } from '@/lib/trpc';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import { ClientSelector } from '@/components/brain/ClientSelector';
import { AreaSelector } from '@/components/brain/AreaSelector';
import { BrainCitationCard } from '@/components/brain/BrainCitationCard';
import { BrainConversationSidebar } from '@/components/brain/BrainConversationSidebar';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import {
  ChatComposer,
  TypingDots,
  StaggerItem,
  AnimatedHeading,
  useAutoResizeTextarea,
} from '@/components/ui/animated-ai-chat';
import {
  brainAreaOf,
  brainScopeName,
  type BrainCitation,
  type BrainQueryHttpResponse,
} from '../../../../shared/types/brain';
import { brainSuggestionsFor } from '../../../../shared/brain-suggestions';

const THINKING_KEYS = [
  'brain.chat.thinking.slack',
  'brain.chat.thinking.history',
  'brain.chat.thinking.revenue',
  'brain.chat.thinking.notes',
  'brain.chat.thinking.nps',
  'brain.chat.thinking.synthesizing',
  'brain.chat.thinking.preparing',
] as const;


// Sugerencias del chat: shared/brain-suggestions.ts (propias del cliente en su JSON
// de shared/brain-clients/, genéricas y de áreas ahí). El smoke test usa las mismas.

interface BrainMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  citations?: BrainCitation[];
  question?: string;
  feedback?: 'like' | 'dislike';
}

function isArtifact(content: string): boolean {
  return content.length >= 600 || /^## /m.test(content) || /^\|.+\|/m.test(content);
}

export function BrainChatPage() {
  const navigate = useNavigate();
  const { conversationId } = useParams<{ conversationId: string }>();
  const { t, i18n } = useTranslation();
  const { isAdmin } = useAuth();
  const [messages, setMessages] = useState<BrainMessage[]>([]);
  const [clientId, setClientId] = useState<string | null>(null);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  // Id of the assistant bubble currently receiving streamed tokens (null when
  // not streaming). Used to hide the "thinking" animation once tokens arrive.
  const [streamingMsgId, setStreamingMsgId] = useState<string | null>(null);
  const [thinkingKey, setThinkingKey] = useState<(typeof THINKING_KEYS)[number]>(THINKING_KEYS[0]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [inputFocused, setInputFocused] = useState(false);
  // El textarea crece con el contenido en vez de scrollear a una altura fija
  // Sol 15/09: con preguntas largas el cuadro (6 renglones, 900px) se hacía
  // incómodo de leer y editar → hasta ~12 renglones y la columna más ancha.
  const { textareaRef, adjustHeight } = useAutoResizeTextarea({ minHeight: 52, maxHeight: 320 });
  const phraseIndexRef = useRef(0);
  // Which conversation the in-memory `messages` belong to — prevents the
  // hydration query from clobbering the thread we just created client-side.
  const loadedConversationIdRef = useRef<string | null>(null);
  // Mirrors the current route param so in-flight responses can detect that
  // the user navigated to another thread while the request was pending.
  const currentConvIdRef = useRef<string | null>(null);
  useEffect(() => {
    currentConvIdRef.current = conversationId ?? null;
  }, [conversationId]);
  // Any route change means we switched threads: a request still in flight
  // belongs to the thread we left (its response is dropped by the originConvId
  // guard), so it no longer owns the spinner. Reset unconditionally — covers
  // new-chat → existing-conversation, which the per-mode effects below miss.
  useEffect(() => {
    setIsLoading(false);
  }, [conversationId]);
  // Mirrors messages.length for the hydration guard (avoids stale closures).
  const messagesCountRef = useRef(0);
  useEffect(() => {
    messagesCountRef.current = messages.length;
  }, [messages]);

  const sessionId = useRef(crypto.randomUUID()).current;
  const utils = trpc.useUtils();
  const submitFeedbackMutation = trpc.brain.submitFeedback.useMutation();
  const [feedbackPending, setFeedbackPending] = useState<string | null>(null);
  const [feedbackComment, setFeedbackComment] = useState('');
  const [attachedSkill, setAttachedSkill] = useState<{ name: string; prompt: string } | null>(null);
  const [contextFiles, setContextFiles] = useState<AttachedContextFile[]>([]);
  const { data: clientsData } = trpc.brain.listClients.useQuery();
  const getContextUploadUrl = trpc.brain.getContextUploadUrl.useMutation();

  // Rehydrate a persisted conversation when visiting /brain/c/:conversationId
  const conversationQuery = trpc.brain.getConversation.useQuery(
    { conversationId: conversationId ?? '' },
    { enabled: !!conversationId, retry: false }
  );

  // Switching to another conversation: clear the stale thread while it loads
  useEffect(() => {
    if (
      conversationId &&
      loadedConversationIdRef.current &&
      loadedConversationIdRef.current !== conversationId
    ) {
      loadedConversationIdRef.current = null;
      setMessages([]);
    }
  }, [conversationId]);

  useEffect(() => {
    if (!conversationId || !conversationQuery.data) return;
    // Guard against react-query briefly handing back a previous route's data
    if (conversationQuery.data.conversationId !== conversationId) return;
    // Don't clobber an in-flight optimistic turn (the user just sent a message)
    if (isLoading) return;
    const stored = conversationQuery.data.messages;
    const alreadyLoaded = loadedConversationIdRef.current === conversationId;
    // Apply server state on first load OR when it's ahead of memory (fresh
    // refetch after a stale cache hit); never downgrade below what we show
    // (protects the just-created first turn from an eventually-consistent read).
    if (alreadyLoaded && stored.length <= messagesCountRef.current) return;
    const hydrated: BrainMessage[] = stored.map((m, i) => ({
      id: m.messageId,
      role: m.role,
      content: m.content,
      citations: m.citations,
      // `question` (needed for feedback) = content of the preceding user message
      question: m.role === 'assistant' && stored[i - 1]?.role === 'user'
        ? stored[i - 1].content
        : undefined,
      feedback: m.feedback,
    }));
    setMessages(hydrated);
    setClientId(conversationQuery.data.clientId);
    loadedConversationIdRef.current = conversationId;
  }, [conversationId, conversationQuery.data, isLoading]);

  useEffect(() => {
    // Only a genuine "gone / not yours" wipes the thread. Transient errors
    // (network blip, 500, refetch failure) must NOT kick the user out.
    const code = (conversationQuery.error?.data as { code?: string } | null | undefined)?.code;
    if (conversationId && (code === 'NOT_FOUND' || code === 'FORBIDDEN')) {
      // 'area_forbidden': el chat es de un área (Finance, Pipeline) que el usuario ya no puede usar.
      const areaForbidden = code === 'FORBIDDEN' && conversationQuery.error?.message === 'area_forbidden';
      toast.error(t(areaForbidden ? 'brain.chat.conversation_area_forbidden' : 'brain.chat.conversation_not_found'));
      navigate('/brain', { replace: true });
    }
  }, [conversationId, conversationQuery.error, navigate, t]);

  // Back to /brain (new chat): reset the thread
  useEffect(() => {
    if (conversationId) return;
    loadedConversationIdRef.current = null;
    setMessages([]);
    setInput('');
    setAttachedSkill(null);
    setContextFiles([]);
  }, [conversationId]);

  // Auto-select first client when data loads (new-chat mode only)
  useEffect(() => {
    if (!conversationId && !clientId && clientsData?.clients?.length) {
      setClientId(clientsData.clients[0]);
    }
  }, [clientsData?.clients, clientId, conversationId]);
  const messagesEndRef = useChatScroll([messages, isLoading]);

  // Rotate thinking phrases while loading
  useEffect(() => {
    if (!isLoading) return;
    const interval = setInterval(() => {
      phraseIndexRef.current = (phraseIndexRef.current + 1) % THINKING_KEYS.length;
      setThinkingKey(THINKING_KEYS[phraseIndexRef.current]);
    }, 1600);
    return () => clearInterval(interval);
  }, [isLoading]);

  const submitQuestion = useCallback(async (q: string) => {
    if (!q || !clientId || isLoading) return;

    // The thread this request belongs to — if the user navigates elsewhere
    // while it's in flight, the response must not land in the wrong thread.
    const originConvId = conversationId ?? null;
    // Re-checked (not captured): the user may navigate mid-stream, and the
    // response must never land in — or corrupt — a thread we've left.
    const stillHere = () => currentConvIdRef.current === originConvId;

    setMessages((prev) => [...prev, { id: crypto.randomUUID(), role: 'user', content: q }]);
    setIsLoading(true);
    phraseIndexRef.current = 0;
    setThinkingKey(THINKING_KEYS[0]);

    let streamMsgId: string | null = null; // temp id of the streaming assistant bubble
    try {
      // Direct Function URL (no CloudFront) — heavy queries exceed the 30s
      // CloudFront timeout on the tRPC router; this endpoint has no such cap.
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      const res = await fetch(import.meta.env.VITE_BRAIN_QUERY_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          clientId,
          question: q,
          language: i18n.language,
          sessionId,
          stream: true,
          // Continue the persisted thread, or start one on the first message.
          // The server rebuilds history from storage when conversationId is set;
          // the in-memory slice below is only a fallback for stateless mode.
          ...(conversationId ? { conversationId } : { persist: true }),
          // Recortado a 4000 como exige el schema del handler — una respuesta
          // larga acá adentro hacía 400ear TODA la conversación (el server
          // igual pisa esto con el historial persistido).
          conversationHistory: messages.slice(-10).map((m) => ({
            role: m.role,
            content: m.content.slice(0, 4000),
          })),
          attachedSkillPrompt: attachedSkill?.prompt,
          contextFileKeys: contextFiles.filter((f) => !f.uploading).map((f) => f.key),
        }),
      });

      // Pre-stream errors keep real HTTP status codes.
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        const code = (errBody as { error?: string }).error;
        if (!stillHere()) return;
        if (code === 'conversation_not_found' || code === 'forbidden' || code === 'client_mismatch') {
          toast.error(t('brain.chat.conversation_not_found'));
          navigate('/brain', { replace: true });
          return;
        }
        if (code === 'conversation_full') {
          setMessages((prev) => [
            ...prev,
            { id: crypto.randomUUID(), role: 'assistant', content: t('brain.chat.conversation_full') },
          ]);
          return;
        }
        throw new Error(`Brain query failed: ${res.status}`);
      }
      if (!res.body) throw new Error('No response stream');

      // Read the NDJSON stream: {type:'meta'} → {type:'delta',text}… → {type:'done',…}
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let acc = '';
      let done: BrainQueryHttpResponse | null = null;

      const ensureBubble = () => {
        if (streamMsgId) return;
        streamMsgId = crypto.randomUUID();
        setStreamingMsgId(streamMsgId);
        if (stillHere()) {
          setMessages((prev) => [...prev, { id: streamMsgId!, role: 'assistant', content: '', question: q }]);
        }
      };

      streamLoop: while (true) {
        const { value, done: rdDone } = await reader.read();
        if (rdDone) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';
        for (const line of lines) {
          if (!line.trim()) continue;
          let ev: { type?: string; text?: string } & Partial<BrainQueryHttpResponse>;
          try { ev = JSON.parse(line); } catch { continue; }
          if (ev.type === 'delta') {
            acc += ev.text ?? '';
            ensureBubble();
            if (stillHere()) {
              const id = streamMsgId;
              setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, content: acc } : m)));
            }
          } else if (ev.type === 'done') {
            done = ev as BrainQueryHttpResponse;
            break streamLoop;
          } else if (ev.type === 'error') {
            throw new Error('stream error');
          }
        }
      }

      // The turn persisted server-side — refresh sidebar + conversation cache.
      if (done?.conversationId) {
        utils.brain.listConversations.invalidate();
        utils.brain.getConversation.invalidate({ conversationId: done.conversationId });
      }
      if (!stillHere()) return;

      // Finalize the streamed bubble: attach citations and swap the temp id for
      // the stored messageId so like/dislike targets the persisted message.
      if (streamMsgId) {
        const tempId = streamMsgId;
        setMessages((prev) => prev.map((m) => m.id === tempId
          ? { ...m, id: done?.assistantMessageId ?? tempId, content: done?.answer || acc || m.content, citations: done?.citations, question: q }
          : m));
      } else {
        // No deltas arrived — render whatever the done event carried.
        setMessages((prev) => [...prev, {
          id: done?.assistantMessageId ?? crypto.randomUUID(),
          role: 'assistant',
          content: done?.answer || acc || t('brain.chat.error_response'),
          citations: done?.citations,
          question: q,
        }]);
      }

      if (done?.conversationId && !originConvId) {
        // First turn of a new conversation: adopt the id without re-hydrating
        loadedConversationIdRef.current = done.conversationId;
        navigate(`/brain/c/${done.conversationId}`, { replace: true });
      }

      // The answer showed but wasn't saved — the conversation hit its size ceiling.
      // Tell the user to start a fresh chat instead of silently losing history.
      if (done?.conversationFull) {
        toast.warning(t('brain.chat.conversation_full'), { duration: 8000 });
      }
    } catch {
      if (stillHere()) {
        if (streamMsgId) {
          const id = streamMsgId;
          // Drop question/citations so the error bubble shows no feedback buttons
          // (consistent with the buffered error path).
          setMessages((prev) => prev.map((m) => m.id === id
            ? { ...m, content: t('brain.chat.error_response'), question: undefined, citations: undefined }
            : m));
        } else {
          setMessages((prev) => [
            ...prev,
            { id: crypto.randomUUID(), role: 'assistant', content: t('brain.chat.error_response') },
          ]);
        }
      }
    } finally {
      // Only clear the shared streaming flag if THIS invocation still owns it — a
      // newer stream on another thread may have taken over the shared state.
      setStreamingMsgId((cur) => (cur === streamMsgId ? null : cur));
      // Only clear the spinner if we're still on the thread that started it
      if (currentConvIdRef.current === originConvId) setIsLoading(false);
    }
  }, [clientId, isLoading, messages, attachedSkill, contextFiles, conversationId, i18n.language, navigate, sessionId, t, utils]);

  const handleSubmit = useCallback(() => {
    const q = input.trim();
    if (!q) return;
    setInput('');
    submitQuestion(q);
  }, [input, submitQuestion]);

  const handleFileUpload = async (file: File) => {
    const { url, key, filename } = await getContextUploadUrl.mutateAsync({
      filename: file.name,
      contentType: file.type || 'application/octet-stream',
    });
    const placeholder: AttachedContextFile = { key, filename, uploading: true };
    setContextFiles((prev) => [...prev, placeholder]);
    await fetch(url, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
    setContextFiles((prev) => prev.map((f) => f.key === key ? { ...f, uploading: false } : f));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const clientDisplayName = clientId ? brainScopeName(clientId) : '';
  const isArea = brainAreaOf(clientId) !== null;
  const suggestions = brainSuggestionsFor(clientId, i18n.language);

  return (
    // Negate main's padding so Brain fills the full viewport height below topbar
    <div className="-m-3 sm:-m-4 md:-m-6 flex h-[calc(100vh-4rem)]">
      {/* Conversation history — desktop column */}
      <aside className="hidden md:flex w-64 shrink-0 flex-col border-r border-border bg-background">
        <BrainConversationSidebar activeConversationId={conversationId} />
      </aside>

      {/* Chat column */}
      <div className="flex-1 min-w-0 flex flex-col">
      {/* Header */}
      <div className="shrink-0 flex items-center justify-between px-4 md:px-6 py-3 border-b border-border bg-background">
        <div className="flex items-center gap-2.5">
          {/* Conversation history — mobile sheet */}
          <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
            <SheetTrigger asChild>
              <button
                className="md:hidden p-1.5 rounded-md border border-border text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                title={t('brain.sidebar.title')}
              >
                <History className="w-4 h-4" />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 p-0">
              <BrainConversationSidebar
                activeConversationId={conversationId}
                onNavigate={() => setSidebarOpen(false)}
              />
            </SheetContent>
          </Sheet>
          <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
            <Brain className="h-4 w-4 text-primary" />
          </div>
          <div>
            <h1 className="text-sm font-semibold leading-none">{t('brain.chat.title')}</h1>
            <p className="text-xs text-muted-foreground mt-0.5">{t('brain.chat.subtitle')}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Client is locked once a conversation exists, and while the
              first-turn request is in flight (avoids a client_mismatch race) */}
          <AreaSelector value={isArea ? clientId : null} onChange={setClientId} disabled={!!conversationId || isLoading} />
          <ClientSelector value={isArea ? null : clientId} onChange={setClientId} disabled={!!conversationId || isLoading} />
          <button
            onClick={() => navigate('/brain/clients/new')}
            className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md border border-border text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            title={t('brain.chat.add_client_title')}
          >
            <Plus className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">{t('brain.chat.add_client_btn')}</span>
          </button>
          {isAdmin && (
            <button
              onClick={() => navigate('/brain/clients/new?tab=salud')}
              className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md border border-border text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              title={t('brain.chat.salud_del_brain')}
            >
              <Activity className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Salud</span>
            </button>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 min-h-0 overflow-y-auto px-4 md:px-6 py-6">
        {conversationId && conversationQuery.isLoading && messages.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <WinclapLoader size={28} color="#1CC9B9" />
          </div>
        ) : messages.length === 0 && !isLoading ? (
          <div className="flex flex-col items-center justify-center h-full gap-5 text-center">
            <div className="h-16 w-16 rounded-2xl bg-primary/10 flex items-center justify-center">
              <Brain className="h-8 w-8 text-primary" />
            </div>
            <div className="max-w-sm">
              <AnimatedHeading
                title={
                  clientId
                    ? t('brain.chat.placeholder_question', { client: clientDisplayName })
                    : t('brain.chat.select_client')
                }
                subtitle={isArea ? t('brain.chat.placeholder_subtitle_area', { area: clientDisplayName }) : t('brain.chat.placeholder_subtitle')}
              />
            </div>
            {clientId && (
              <div className="flex flex-wrap gap-2 justify-center max-w-lg">
                {/* Quick-start: fires the full status question straight away (clientes; un área no tiene "status") */}
                {!isArea && (
                  <button
                    onClick={() => submitQuestion(t('brain.chat.status_prompt', { client: clientDisplayName }))}
                    disabled={isLoading}
                    className="text-xs px-3 py-2 rounded-full border border-primary/40 bg-primary/5 hover:bg-primary/10 text-primary font-medium transition-colors text-left flex items-center gap-1.5"
                  >
                    <Sparkles className="w-3 h-3" />
                    {t('brain.chat.status_suggestion', { client: clientDisplayName })}
                  </button>
                )}
                {suggestions.map((s, i) => (
                  <StaggerItem key={s} index={i + 1}>
                    <button
                      onClick={() => setInput(s)}
                      className="text-xs px-3 py-2 rounded-full border border-border bg-muted/50 hover:bg-muted hover:border-foreground/20 text-muted-foreground hover:text-foreground transition-colors text-left"
                    >
                      {s}
                    </button>
                  </StaggerItem>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-6 max-w-5xl mx-auto">
            {messages.map((msg) =>
              msg.role === 'user' ? (
                <div key={msg.id} className="flex justify-end">
                  <div className="max-w-[80%] bg-primary text-primary-foreground rounded-2xl rounded-tr-sm px-4 py-3">
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  </div>
                </div>
              ) : (
                <div key={msg.id} className="flex gap-3 items-start">
                  <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                    <Brain className="h-3.5 w-3.5 text-primary" />
                  </div>
                  <div className="flex-1 flex flex-col gap-3 min-w-0">
                    {isArtifact(msg.content) ? (
                      <div className="rounded-xl border border-border bg-card overflow-hidden">
                        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border bg-muted/30">
                          <span className="text-[10px] text-primary font-semibold tracking-wider uppercase">
                            ✦ {t('brain.chat.analysis_label')}
                          </span>
                        </div>
                        <div className="p-5">
                          <BrainMarkdown content={msg.content} />
                        </div>
                      </div>
                    ) : (
                      <div className="bg-muted rounded-2xl rounded-tl-sm px-4 py-3">
                        <BrainMarkdown content={msg.content} />
                      </div>
                    )}
                    {msg.citations && msg.citations.length > 0 && (
                      <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-none [&::-webkit-scrollbar]:hidden">
                        {msg.citations.map((c) => (
                          <BrainCitationCard key={c.pageId} citation={c} />
                        ))}
                      </div>
                    )}

                    {/* Feedback */}
                    {!isLoading && clientId && msg.question && (
                      <div className="flex flex-col gap-2">
                        {msg.feedback ? (
                          <span className="text-xs text-muted-foreground flex items-center gap-1.5">
                            {msg.feedback === 'like'
                              ? <><ThumbsUp className="w-3 h-3 text-primary" /> {t('brain.chat.feedback_thanks')}</>
                              : <><ThumbsDown className="w-3 h-3 text-destructive" /> {t('brain.chat.feedback_thanks')}</>
                            }
                          </span>
                        ) : feedbackPending === msg.id ? (
                          <div className="flex flex-col gap-2 max-w-sm">
                            <p className="text-xs text-muted-foreground">{t('brain.chat.feedback_dislike_prompt')}</p>
                            <textarea
                              autoFocus
                              value={feedbackComment}
                              onChange={(e) => setFeedbackComment(e.target.value)}
                              placeholder={t('brain.chat.feedback_dislike_placeholder')}
                              rows={2}
                              className="w-full text-xs rounded-lg border border-border bg-muted/50 px-3 py-2 resize-none focus:outline-hidden focus:ring-1 focus:ring-primary"
                            />
                            <div className="flex gap-2">
                              <button
                                disabled={submitFeedbackMutation.isPending}
                                onClick={async () => {
                                  setMessages((prev) => prev.map((m) => m.id === msg.id ? { ...m, feedback: 'dislike' } : m));
                                  setFeedbackPending(null);
                                  setFeedbackComment('');
                                  await submitFeedbackMutation.mutateAsync({
                                    messageId:       msg.id,
                                    clientId:        clientId!,
                                    question:        msg.question!,
                                    answerPreview:   msg.content.slice(0, 500),
                                    feedback:        'dislike',
                                    feedbackComment: feedbackComment || undefined,
                                    sessionId,
                                    conversationId,
                                  }).catch(() => {});
                                }}
                                className="text-xs px-3 py-1.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                              >
                                {t('brain.chat.feedback_send')}
                              </button>
                              <button
                                onClick={() => { setFeedbackPending(null); setFeedbackComment(''); }}
                                className="text-xs px-3 py-1.5 rounded-md border border-border text-muted-foreground hover:text-foreground transition-colors"
                              >
                                {t('brain.chat.cancel')}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1">
                            <button
                              disabled={submitFeedbackMutation.isPending}
                              onClick={async () => {
                                setMessages((prev) => prev.map((m) => m.id === msg.id ? { ...m, feedback: 'like' } : m));
                                await submitFeedbackMutation.mutateAsync({
                                  messageId:     msg.id,
                                  clientId:      clientId!,
                                  question:      msg.question!,
                                  answerPreview: msg.content.slice(0, 500),
                                  feedback:      'like',
                                  sessionId,
                                  conversationId,
                                }).catch(() => {});
                              }}
                              title={t('brain.chat.feedback_like')}
                              className="p-1.5 rounded-md text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
                            >
                              <ThumbsUp className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => { setFeedbackPending(msg.id); setFeedbackComment(''); }}
                              title={t('brain.chat.feedback_dislike')}
                              className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                            >
                              <ThumbsDown className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )
            )}

            {/* Thinking animation — hidden once the answer starts streaming */}
            {isLoading && !streamingMsgId && (
              <div className="flex gap-3 items-start">
                <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                  <WinclapLoader size={16} color="#1CC9B9" />
                </div>
                <div className="bg-muted rounded-2xl rounded-tl-sm px-4 py-3">
                  <p className="flex items-center gap-1.5 text-sm italic text-muted-foreground">
                    {t(thinkingKey)}
                    <TypingDots />
                  </p>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Composer — una sola superficie: el textarea arriba y las acciones
          abajo, en vez de cuatro controles sueltos en fila. */}
      <div className="shrink-0 bg-background px-4 md:px-6 pb-3 pt-2">
        <div className="mx-auto max-w-5xl">
          <AttachmentBar
            skill={attachedSkill}
            contextFiles={contextFiles}
            onRemoveSkill={() => setAttachedSkill(null)}
            onRemoveFile={(key) => setContextFiles((prev) => prev.filter((f) => f.key !== key))}
          />
          <ChatComposer
            focused={inputFocused}
            footer={
              <>
                <div className="flex items-center gap-1">
                  <SkillPickerPopover
                    value={attachedSkill}
                    onChange={setAttachedSkill}
                    disabled={!clientId || isLoading}
                  />
                  <FilePicker
                    files={contextFiles}
                    onAdd={(f) => setContextFiles((prev) => [...prev, f])}
                    onRemove={(key) => setContextFiles((prev) => prev.filter((f) => f.key !== key))}
                    onUpload={handleFileUpload}
                    disabled={!clientId || isLoading}
                  />
                </div>
                <Button
                  onClick={handleSubmit}
                  disabled={!input.trim() || !clientId || isLoading}
                  size="sm"
                  className="h-8 gap-1.5 rounded-lg px-3"
                >
                  {isLoading ? <WinclapLoader size={14} /> : <Send className="h-3.5 w-3.5" />}
                  <span className="text-xs font-medium">{t('brain.chat.send', 'Enviar')}</span>
                </Button>
              </>
            }
          >
            <Textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => {
                setInput(e.target.value);
                adjustHeight();
              }}
              onKeyDown={handleKeyDown}
              onFocus={() => setInputFocused(true)}
              onBlur={() => setInputFocused(false)}
              placeholder={
                clientId
                  ? t('brain.chat.placeholder_ask', { client: clientDisplayName })
                  : t('brain.chat.placeholder_no_client')
              }
              disabled={!clientId || isLoading}
              maxLength={6000}
              // El borde y el fondo los pone el composer: acá sólo el texto.
              className="max-h-80 min-h-[52px] resize-none overflow-y-auto border-0 bg-transparent px-4 py-3 text-[15px] leading-6 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
            />
          </ChatComposer>
          <p className="mt-2 text-center text-[11px] text-muted-foreground">
            {t('brain.chat.disclaimer')}
          </p>
        </div>
      </div>
      </div>
    </div>
  );
}
